import React, { useContext, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useParams, useLocation, Link } from "react-router-dom"
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Stack,
  Menu,
  MenuItem,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SlideshowIcon from "@mui/icons-material/SlideshowOutlined"
import { useAuthContext } from "context/auth/authContext"
import { useSubscriptionContext } from "context/subscription/subscriptionContext"
import ProjectContext from "context/projects/projectContext"
import i18n from "i18n"
import DropdownSettings from "components/Project/Overview/DropdownSettings"
import ModalClosePassword from "components/CommonForBoth/TopbarDropdown/ModalClosePassword"
import cloudCheck from "assets/customIcons/cloudCheck.svg"
import {
  NAVBAR_CONFIG_BY_ROUTE,
  DEFAULT_NAVBAR_CONFIG,
} from "./navbarConfig.constants"

const HEADER_HEIGHT = 52
const HEADER_ROW_HEIGHT = 46
function HeaderTitle({ children }) {
  return (
    <Typography
      variant="inherit"
      component="h1"
      sx={{
        fontWeight: "normal !important",
        fontSize: "1.1rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: "#414141",
      }}
    >
      {children}
    </Typography>
  )
}

HeaderTitle.propTypes = {
  children: PropTypes.node,
}

function AppBarHeader({ leftOffset = 60 }) {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { user } = useAuthContext()
  const { subscriptionState, isTrial, trialDays, noSubscription, daysLeft } =
    useSubscriptionContext()
  const { getProject, projectInfo } = useContext(ProjectContext)
  const location = useLocation()
  const pathname = location.pathname
  const matchedNavbarConfig = NAVBAR_CONFIG_BY_ROUTE.find(config =>
    config.match({ pathname, projectId }),
  )
  const activeNavbarConfig = matchedNavbarConfig ?? DEFAULT_NAVBAR_CONFIG
  const activeNavGroups = activeNavbarConfig.navGroups
  const [openModal, setOpenModal] = useState(false)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState(null)
  const openGroup =
    openMenuId != null
      ? activeNavGroups.find(g => g.labelKey === openMenuId)
      : null

  const resolveRoutePath = path => (projectId ? `${path}/${projectId}` : path)

  const doesPathMatch = routePath => {
    if (routePath === "/invoices" && pathname.startsWith("/invoice/")) {
      return true
    }
    if (
      routePath === "/purchase-orders" &&
      pathname.startsWith("/purchase-order/")
    ) {
      return true
    }

    return pathname === routePath || pathname.startsWith(`${routePath}/`)
  }

  const navRoutePaths = activeNavGroups.flatMap(group =>
    group.children
      ? group.children.map(child => resolveRoutePath(child.to))
      : [resolveRoutePath(group.to)],
  )

  const bestMatchingNavPath = navRoutePaths
    .filter(doesPathMatch)
    .sort((a, b) => b.length - a.length)[0]

  const shouldShowNavigation = !!projectId || activeNavbarConfig.forceNavigation
  useEffect(() => {
    if (projectId && getProject) {
      getProject(projectId)
    }
  }, [projectId, getProject])

  const projectName =
    projectInfo?.projectId === projectId ? projectInfo?.name : null

  const handleBack = () => {
    navigate("/dashboard")
  }

  const trialBlock =
    user && subscriptionState && (isTrial || noSubscription) ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 23,
              height: 23,
              flexShrink: 0,
              borderRadius: "50%",
              border: "2px solid",
              borderColor:
                daysLeft === 0
                  ? "#BF2929"
                  : daysLeft <= trialDays / 2
                    ? "#EE950F"
                    : "#519F62",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              component="span"
              sx={{
                color: "#414141",
                fontSize: "12px",
                fontWeight: 700,
                fontFamily: "Helvetica, Arial, sans-serif",
                lineHeight: 1,
              }}
            >
              {daysLeft}
            </Typography>
          </Box>
          <Typography
            component="span"
            onClick={() => navigate("/subscription")}
            sx={{
              color: "#5984FC",
              fontWeight: 700,
              fontSize: "14px !important",
              cursor: "pointer",
              textDecoration: "underline",
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {i18n.t("Elige tu plan")}
          </Typography>
        </Box>
        <Button
          color="branded"
          variant="contained"
          component="a"
          size="small"
          href="https://meetings-eu1.hubspot.com/meetings/tizianopierini/trial-demo-"
          target="_blank"
          rel="noreferrer"
          startIcon={<SlideshowIcon />}
          sx={{
            minWidth: "80px",
            textTransform: "none",
          }}
        >
          Demo
        </Button>
      </Box>
    ) : null

  if (!shouldShowNavigation) {
    return (
      <AppBar
        elevation={0}
        sx={{
          position: "relative",
          width: "auto",
          marginLeft: `${leftOffset}px`,
          height: HEADER_HEIGHT,
          minHeight: HEADER_HEIGHT,
          backgroundColor: "white",
          color: "#000",
          boxShadow: "0px 0px 9px 0px #3B3B3B1A",
          "& .MuiToolbar-root": {
            minHeight: HEADER_HEIGHT,
            paddingLeft: 3,
            paddingRight: 2,
          },
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            minHeight: HEADER_HEIGHT,
          }}
        >
          <HeaderTitle>{i18n.t(activeNavbarConfig.listTitleKey)}</HeaderTitle>
          {trialBlock}
        </Toolbar>
      </AppBar>
    )
  }

  return (
    <>
      <AppBar
        elevation={0}
        sx={{
          position: "relative",
          width: "auto",
          marginLeft: `${leftOffset}px`,
          minHeight: HEADER_HEIGHT,
          backgroundColor: "white",
          color: "#000",
          boxShadow: "0px 0px 9px 0px #3B3B3B1A",
        }}
      >
        {/* Row 1: Back + project name (izquierda) | cog (derecha) */}
        <Toolbar
          sx={{
            height: HEADER_HEIGHT,
            minHeight: { xs: HEADER_HEIGHT, sm: HEADER_HEIGHT },
            paddingLeft: 2,
            paddingRight: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {activeNavbarConfig.showBackButton ? (
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={i18n.t("Volver")}
              sx={{ p: 0.5 }}
            >
              <ArrowBackIcon sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          ) : null}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: "calc(100% - 100px)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HeaderTitle>
                {projectName ?? i18n.t(activeNavbarConfig.detailTitleKey)}
              </HeaderTitle>

              {activeNavbarConfig.showCloudCheck ? (
                <img
                  src={cloudCheck}
                  style={{
                    width: "22px",
                    height: "22px",
                  }}
                />
              ) : null}
            </Box>
          </Box>
          {trialBlock}
        </Toolbar>

        {/* Row 2: Nav tabs + right options */}
        <Toolbar
          sx={{
            height: HEADER_ROW_HEIGHT,
            minHeight: { xs: HEADER_ROW_HEIGHT, sm: HEADER_ROW_HEIGHT },
            paddingLeft: 3,
            paddingRight: 2,
            gap: 3,
            alignItems: "center",
          }}
        >
          <Stack
            direction="row"
            spacing={0}
            alignItems="center"
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 400,
              "& a": { textDecoration: "none", color: "inherit" },
            }}
          >
            {activeNavGroups.map(group => {
              const groupActive = group.children
                ? group.children.some(
                    c => resolveRoutePath(c.to) === bestMatchingNavPath,
                  )
                : resolveRoutePath(group.to) === bestMatchingNavPath
              const areAllChildrenDisabled =
                !!group.children &&
                group.children.every(child => child.disabled)
              const isGroupDisabled = !!group.disabled || areAllChildrenDisabled
              const sxTab = {
                borderBottom: "2px solid transparent",
                borderColor: groupActive ? "#1A63EE" : "transparent",
                fontWeight: 700,
                paddingInline: "12px",
                backgroundColor: "transparent !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: HEADER_ROW_HEIGHT,
                minHeight: HEADER_ROW_HEIGHT,
                color: isGroupDisabled
                  ? "#B5B5B5"
                  : groupActive
                    ? "#1A63EE"
                    : "#8E8E8E",
                cursor: isGroupDisabled ? "not-allowed" : "pointer",
                pb: 0.5,
                borderRadius: 0,
                "&:hover": {
                  borderColor: groupActive ? "#1A63EE" : "transparent",
                  color: isGroupDisabled
                    ? "#B5B5B5"
                    : groupActive
                      ? "#1A63EE"
                      : "#414141",
                },
              }

              if (group.children) {
                const open = openMenuId === group.labelKey
                return (
                  <Button
                    key={group.labelKey}
                    disabled={isGroupDisabled}
                    endIcon={
                      <Box
                        sx={{
                          backgroundColor: open ? "#EEEEEE" : "transparent",
                          width: "24px",
                          height: "24px",
                          borderRadius: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform: open ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                            color: "text.secondary",
                          }}
                        />
                      </Box>
                    }
                    onClick={e => {
                      setMenuAnchorEl(open ? null : e.currentTarget)
                      setOpenMenuId(open ? null : group.labelKey)
                    }}
                    sx={{
                      ...sxTab,
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "3px 12px",
                      borderRadius: 0,
                      fontWeight: 700,
                      "&.MuiButton-root": { minWidth: "auto" },
                    }}
                  >
                    {i18n.t(group.labelKey)}
                  </Button>
                )
              }

              const to = projectId ? `${group.to}/${projectId}` : group.to

              if (isGroupDisabled) {
                return (
                  <Box
                    key={group.labelKey}
                    component="span"
                    aria-disabled="true"
                    sx={{ minWidth: "min-content", display: "flex" }}
                  >
                    <Box component="span" sx={sxTab}>
                      {i18n.t(group.labelKey)}
                    </Box>
                  </Box>
                )
              }

              return (
                <Link
                  key={group.labelKey}
                  to={to}
                  style={{ minWidth: "min-content", display: "flex" }}
                >
                  <Box component="span" sx={sxTab}>
                    {i18n.t(group.labelKey)}
                  </Box>
                </Link>
              )
            })}
          </Stack>
          <Menu
            anchorEl={menuAnchorEl}
            open={!!openGroup}
            onClose={() => {
              setOpenMenuId(null)
              setMenuAnchorEl(null)
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            MenuListProps={{ autoFocus: false }}
            PaperProps={{
              sx: {
                borderRadius: 1.5,
                boxShadow: "0px 4px 20px rgba(0,0,0,0.12)",
                mt: 0.5,
              },
            }}
          >
            {openGroup?.children?.map(child => {
              const to = projectId ? `${child.to}/${projectId}` : child.to
              return (
                <MenuItem
                  key={child.labelKey}
                  disabled={!!child.disabled}
                  component={Link}
                  to={to}
                  onClick={() => {
                    setOpenMenuId(null)
                    setMenuAnchorEl(null)
                  }}
                  sx={{
                    fontWeight: 400,
                    color: "#000 !important",
                    backgroundColor: "transparent",
                    "&.Mui-selected": {
                      backgroundColor: "transparent !important",
                      color: "#000 !important",
                    },
                    "&:hover": {
                      backgroundColor: "#FDEFC6 !important",
                      color: "#000 !important",
                    },
                    "&.Mui-focusVisible": {
                      color: "#000 !important",
                    },
                  }}
                >
                  {i18n.t(child.labelKey)}
                </MenuItem>
              )
            })}
          </Menu>
          {projectId &&
          activeNavbarConfig.showHeaderOptions &&
          user &&
          !user.freeTrialExpired &&
          user["custom:role"] !== "WORKER" ? (
            <Box component="span" sx={{ flexShrink: 0 }}>
              <DropdownSettings />
            </Box>
          ) : null}
        </Toolbar>
      </AppBar>
      <ModalClosePassword
        openPassword={openModal}
        setOpenPassword={setOpenModal}
      />
    </>
  )
}

AppBarHeader.propTypes = {
  leftOffset: PropTypes.number,
}

export default AppBarHeader
export { HEADER_HEIGHT }
